import React, { useCallback, useId, useState } from 'react';
import {
  Button,
  Combobox,
  FieldLabel,
  Flex,
  Textarea,
  Tooltip,
} from '@servicetitan/anvil2';
import { BodyText, Headline } from '@servicetitan/design-system';
import { SingleOptionPropStrict } from '@servicetitan/design-system/dist/components/OptionList/Option';
import { TaskOutcome } from '@api/generated/graphql';
import { JobIdLookup } from '@components/JobIdLookup';
import { FeatureFlagKeys, useFeatureFlag } from '@modules/FeatureFlagModule';
import { inboxLocators } from '@locators/inbox';
import { getTestIdProps } from '@lib/testUtils';
import styles from './LogOutcomeSection.module.css';
import strings from './strings';

export interface LogOutcomeSectionProps {
  /** "Answered" outcomes shown in the dropdown's second (answered) group. */
  liveAnswerOutcomeOptions: TaskOutcome[];
  /** "No answer" outcomes shown in the dropdown's first group. */
  noAnswerOutcomeOptions: TaskOutcome[];
  /**
   * Tenant the lookup is scoped to. Required when the Job-ID gate is rendered;
   * the underlying `JobIdLookup` is a no-op without it.
   */
  tenantId?: string;
  /**
   * Fires when the agent submits the form. `notes` is the (possibly empty)
   * call-description text. `jobId` is only forwarded when the FF gate is on
   * AND the agent has resolved a job for a success outcome — non-success
   * outcomes always pass `undefined`.
   */
  onSubmit: (
    outcome: TaskOutcome,
    notes?: string,
    jobId?: string,
  ) => Promise<void>;
  /** Whether the active call has ended; controls outcome + submit availability. */
  isCallEnded: boolean;
}

/** Display option used by the Combobox to render a single outcome row. */
interface CallOutcomeOption {
  /** Label shown to the agent (formatted from the enum value). */
  text: string;
  /** Underlying `TaskOutcome` enum value sent to `onSubmit`. */
  value: TaskOutcome;
  /** Numeric prefixes control group sort order; `groupToString` maps to display labels. */
  group: '1_no_answer' | '2_answered';
}

const GROUP_LABELS: Record<string, string> = {
  '1_no_answer': 'NO ANSWER',
  '2_answered': 'ANSWERED',
};

/**
 * Outcomes whose semantics imply a successful booking (and therefore require
 * an attached Job ID under the FF gate). Currently only `Booked`; future
 * success outcomes can be added here without touching the gating logic.
 */
const SUCCESSFUL_OUTCOMES: ReadonlySet<TaskOutcome> = new Set([
  TaskOutcome.Booked,
]);

/**
 * Format a `TaskOutcome` enum value as a human-readable label by splitting on
 * underscores and title-casing each token (e.g. `LEFT_VOICEMAIL` →
 * `Left Voicemail`).
 */
const formatEnumToDisplay = (enumValue: string): string =>
  enumValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// CCP-39878: Temporarily hide the "Do Not Contact" outcome from manual logging
// per legal request. Backend still returns it in the option lists; filter it on
// the frontend so it can be restored without a backend change.
const HIDDEN_OUTCOMES: ReadonlySet<TaskOutcome> = new Set([
  TaskOutcome.DoNotContact,
]);

/**
 * Build the Combobox option list from the BFF-provided outcome groups,
 * filtering out hidden outcomes (`DoNotContact`) and stamping each option
 * with its display group prefix so the dropdown groups in the right order.
 */
const buildOutcomeOptions = (
  noAnswerOptions: TaskOutcome[],
  liveAnswerOptions: TaskOutcome[],
): CallOutcomeOption[] => [
  ...noAnswerOptions
    .filter((value) => !HIDDEN_OUTCOMES.has(value))
    .map((value) => ({
      text: formatEnumToDisplay(value),
      value,
      group: '1_no_answer' as const,
    })),
  ...liveAnswerOptions
    .filter((value) => !HIDDEN_OUTCOMES.has(value))
    .map((value) => ({
      text: formatEnumToDisplay(value),
      value,
      group: '2_answered' as const,
    })),
];

/**
 * Post-call outcome form for Speed-to-Lead outbound tasks. Lets the agent
 * pick an outcome, write a call description, and (under the
 * `featureJobIdForBookedPreviewDialerOutbounds` flag, when the outcome is a
 * success) resolve an attached Job before submitting. Forwards the resolved
 * `jobId` to `onSubmit` so callers can attach a JobBooking activity to the
 * interaction.
 */
export const LogOutcomeSection: React.FC<LogOutcomeSectionProps> = ({
  liveAnswerOutcomeOptions,
  noAnswerOutcomeOptions,
  tenantId,
  onSubmit,
  isCallEnded,
}) => {
  const [callOutcome, setCallOutcome] = useState<CallOutcomeOption | null>(
    null,
  );
  const [notes, setNotes] = useState('');
  const [resolvedJobId, setResolvedJobId] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isJobIdGateEnabled = useFeatureFlag(
    FeatureFlagKeys.featureJobIdForBookedPreviewDialerOutbounds,
    { defaultValue: false },
  );

  const submitHelpId = useId();

  const outcomeOptions = buildOutcomeOptions(
    noAnswerOutcomeOptions,
    liveAnswerOutcomeOptions,
  );

  const isSuccessfulOutcome =
    !!callOutcome && SUCCESSFUL_OUTCOMES.has(callOutcome.value);
  const shouldShowJobIdGate =
    isJobIdGateEnabled && isSuccessfulOutcome && !!tenantId;
  const isJobIdSatisfied = !shouldShowJobIdGate || !!resolvedJobId;

  const isSubmitEnabled =
    isCallEnded && !!callOutcome && !isSubmitting && isJobIdSatisfied;

  const handleOutcomeChange = useCallback((next: CallOutcomeOption | null) => {
    setCallOutcome(next);
    // When the agent moves away from a success outcome, the gated job state
    // must clear; preserving it would let a stale id ship with a later
    // non-success outcome. Notes intentionally remain — only the gated state
    // is bound to the outcome.
    if (!next || !SUCCESSFUL_OUTCOMES.has(next.value)) {
      setResolvedJobId(undefined);
    }
  }, []);

  const handleJobLookupChange = useCallback(
    (option: SingleOptionPropStrict) => {
      setResolvedJobId(
        typeof option?.value === 'string' ? option.value : undefined,
      );
    },
    [],
  );

  const handleSubmitLogOutcome = async () => {
    if (!isSubmitEnabled) return;

    setIsSubmitting(true);
    try {
      await onSubmit(callOutcome.value, notes, resolvedJobId);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disabled-submit helper text varies based on which gate is failing so the
  // agent can fix the right thing without guessing.
  const disabledHelp = (() => {
    if (!isCallEnded || !callOutcome)
      return strings.labels.submitDisabledTooltip;
    if (shouldShowJobIdGate && !resolvedJobId)
      return strings.labels.submitDisabledJobIdRequired;
    return null;
  })();

  return (
    <Flex direction='column' gap='6' className={styles.container}>
      <Headline size='medium' className={styles.heading}>
        {strings.labels.heading}
      </Headline>

      <Textarea
        label={strings.labels.taskNotes}
        placeholder={strings.placeholders.taskNotes}
        rows={4}
        value={notes}
        onChange={(_, data) => {
          if (typeof data?.value === 'string') {
            setNotes(data.value);
          }
        }}
        disabled={isSubmitting}
        disableResize
      />

      <div className={styles.comboboxContainer}>
        <Combobox.Select
          items={outcomeOptions}
          itemToString={(item) => item?.text ?? ''}
          onChange={handleOutcomeChange}
          selectedItem={callOutcome}
          disabled={!isCallEnded || isSubmitting}
          groupBy='group'
          groupToString={(groupValue) => GROUP_LABELS[groupValue] ?? null}
        >
          <Combobox.SelectTrigger
            label={strings.labels.callOutcome}
            required
            placeholder={strings.placeholders.callOutcome}
          />
          <Combobox.Content>
            {({ items }) => (
              <Combobox.List>
                {items.map((item, i) => (
                  <Combobox.Item key={item.value} item={item} index={i}>
                    {item.text}
                  </Combobox.Item>
                ))}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Select>
      </div>

      {shouldShowJobIdGate && tenantId && (
        <div className={styles.jobIdField}>
          <FieldLabel
            htmlFor={inboxLocators.taskList.speedToLeadLogOutcomeJobIdInput}
            required
          >
            {strings.labels.jobId}
          </FieldLabel>
          <JobIdLookup
            tenantId={tenantId}
            onChange={handleJobLookupChange}
            checkAlreadyLinked
            appearance='anvil2'
            locators={{
              input: inboxLocators.taskList.speedToLeadLogOutcomeJobIdInput,
              button:
                inboxLocators.taskList.speedToLeadLogOutcomeJobIdSearchButton,
              foundCard:
                inboxLocators.taskList.speedToLeadLogOutcomeJobIdFoundCard,
              selectedCard:
                inboxLocators.taskList.speedToLeadLogOutcomeJobIdSelectedCard,
            }}
          />
        </div>
      )}

      {disabledHelp && !isSubmitting && (
        <BodyText
          id={submitHelpId}
          size='small'
          subdued
          {...getTestIdProps(
            inboxLocators.taskList.speedToLeadLogOutcomeSubmitHelp,
          )}
        >
          {disabledHelp}
        </BodyText>
      )}

      <Tooltip>
        <Tooltip.Trigger>
          <Button
            appearance='primary'
            size='medium'
            onClick={handleSubmitLogOutcome}
            disabled={!isSubmitEnabled}
            loading={isSubmitting}
            aria-describedby={
              disabledHelp && !isSubmitting ? submitHelpId : undefined
            }
            {...getTestIdProps(
              inboxLocators.taskList.speedToLeadLogOutcomeSubmit,
            )}
          >
            {strings.labels.submitButton}
          </Button>
        </Tooltip.Trigger>
        {!isSubmitEnabled && !isSubmitting && disabledHelp && (
          <Tooltip.Content>{disabledHelp}</Tooltip.Content>
        )}
      </Tooltip>
    </Flex>
  );
};
